/**
 * Canonical currency-value model shared by AI extraction/enrichment, manual
 * entry, the database write paths, and every display surface.
 *
 * A currency value is `{ amount, currency }` where `amount` is an **unformatted
 * numeric string** (no symbols or commas, e.g. `"5000000"`) and `currency` is an
 * ISO-4217 code (defaults to `"USD"`). Canonical company columns store the two
 * parts split (`annual_revenue` + `annual_revenue_currency`); deal responses
 * store the JSON-serialized object. All conversions go through the helpers here
 * so an object can never leak into a scalar column and a garbage/legacy value
 * (e.g. the literal string `"[object Object]"`) never renders.
 */
import { z } from 'zod';
export const CurrencyValueSchema = z.object({
    amount: z.string(),
    currency: z.string(),
});
const DEFAULT_CURRENCY = 'USD';
/**
 * Coerces an unknown amount into an unformatted numeric string, or `''` when it
 * is not a finite number. Strips `$`, commas, and whitespace. Anything that is
 * not numeric after cleaning (objects, `"[object Object]"`, free text) → `''`.
 */
function coerceAmount(raw) {
    if (typeof raw === 'number')
        return Number.isFinite(raw) ? String(raw) : '';
    if (typeof raw !== 'string')
        return '';
    const cleaned = raw.replace(/[$,\s]/g, '');
    if (!cleaned)
        return '';
    // Reject non-numeric strings (covers the legacy "[object Object]" corruption).
    if (!/^-?\d*\.?\d+$/.test(cleaned))
        return '';
    return cleaned;
}
function coerceCurrency(raw) {
    if (typeof raw === 'string' && raw.trim())
        return raw.trim().toUpperCase();
    return DEFAULT_CURRENCY;
}
/**
 * The single coercion used everywhere. Accepts a `{ amount, currency }` object,
 * a JSON string, a bare number/numeric string, a double-nested
 * `{ amount: { amount, currency } }`, or garbage. Returns the canonical
 * `CurrencyValue`, or `null` when no usable numeric amount is present.
 */
export function normalizeCurrencyValue(raw) {
    if (raw === null || raw === undefined || raw === '')
        return null;
    if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if (!trimmed)
            return null;
        if (trimmed.startsWith('{')) {
            try {
                return normalizeCurrencyValue(JSON.parse(trimmed));
            }
            catch {
                // Not JSON - fall through and treat the string as a bare amount.
            }
        }
        const amount = coerceAmount(trimmed);
        return amount ? { amount, currency: DEFAULT_CURRENCY } : null;
    }
    if (typeof raw === 'number') {
        const amount = coerceAmount(raw);
        return amount ? { amount, currency: DEFAULT_CURRENCY } : null;
    }
    if (typeof raw === 'object') {
        const obj = raw;
        if (!('amount' in obj) && !('currency' in obj))
            return null;
        let amountSource = obj.amount;
        let currencyRaw = obj.currency;
        // Flatten a double-nested `{ amount: { amount, currency } }`, preferring the
        // outer currency when present and falling back to the inner one.
        if (amountSource !== null && typeof amountSource === 'object') {
            const inner = amountSource;
            amountSource = inner.amount;
            if (currencyRaw === null || currencyRaw === undefined || currencyRaw === '') {
                currencyRaw = inner.currency;
            }
        }
        const amount = coerceAmount(amountSource);
        return amount ? { amount, currency: coerceCurrency(currencyRaw) } : null;
    }
    return null;
}
/**
 * Display/edit-oriented wrapper around {@link normalizeCurrencyValue}. Always
 * returns a `CurrencyValue` so form controls have a stable shape; when no usable
 * amount exists it returns `{ amount: '', currency }`, preserving any currency
 * hint carried on the raw value so the user's currency selection survives.
 */
export function parseCurrencyValue(raw, defaultCurrency = DEFAULT_CURRENCY) {
    const normalized = normalizeCurrencyValue(raw);
    if (normalized)
        return normalized;
    let currency = defaultCurrency;
    if (raw !== null &&
        typeof raw === 'object' &&
        typeof raw.currency === 'string' &&
        raw.currency !== '') {
        currency = String(raw.currency)
            .trim()
            .toUpperCase();
    }
    return { amount: '', currency };
}
/** Currency symbols → ISO-4217 codes, for parsing human-written amounts. */
const CURRENCY_SYMBOLS = {
    $: 'USD',
    '€': 'EUR',
    '£': 'GBP',
    '¥': 'JPY',
    '₹': 'INR',
};
/** Magnitude suffixes the extraction model commonly emits (e.g. "$85M", "1.2bn"). */
const MAGNITUDE = {
    k: 1e3,
    thousand: 1e3,
    m: 1e6,
    mm: 1e6,
    mn: 1e6,
    million: 1e6,
    b: 1e9,
    bn: 1e9,
    billion: 1e9,
};
/** Known ISO-4217 codes accepted when written out (keeps "ARR"/"the" from matching). */
const KNOWN_CODES = new Set([
    'USD',
    'EUR',
    'GBP',
    'JPY',
    'INR',
    'CAD',
    'AUD',
    'CHF',
    'CNY',
    'SGD',
    'HKD',
    'NZD',
    'SEK',
    'NOK',
    'DKK',
    'ZAR',
    'BRL',
    'MXN',
]);
/**
 * Parse a human-written currency expression ("$85M", "85 million", "1.2bn",
 * "$85,000,000") into the canonical {@link CurrencyValue}, or `null` when no
 * amount can be recovered. Already-structured inputs (a `{ amount, currency }`
 * object, a JSON string, a bare number, or a plain/`$`-prefixed numeric string)
 * pass straight through {@link normalizeCurrencyValue}, so this is idempotent.
 *
 * Used to coerce AI-extracted currency fields before storage: the extraction
 * model frequently returns a display string ("$85M") rather than the canonical
 * JSON, which the scalar currency parsers reject and the form then shows empty.
 */
export function parseHumanCurrency(raw, defaultCurrency = DEFAULT_CURRENCY) {
    // Structured / already-numeric inputs (object, JSON string, "85000000",
    // number, "$85,000,000" - coerceAmount strips `$` and commas).
    const direct = normalizeCurrencyValue(raw);
    if (direct)
        return direct;
    if (typeof raw !== 'string')
        return null;
    const text = raw.trim();
    if (!text)
        return null;
    // Currency: a symbol wins; else a standalone known ISO code; else the default.
    let currency = defaultCurrency;
    for (const [sym, code] of Object.entries(CURRENCY_SYMBOLS)) {
        if (text.includes(sym)) {
            currency = code;
            break;
        }
    }
    if (currency === defaultCurrency) {
        for (const token of text.toUpperCase().split(/[^A-Z]+/)) {
            if (KNOWN_CODES.has(token)) {
                currency = token;
                break;
            }
        }
    }
    // First number, with an optional adjacent magnitude suffix (multi-char first).
    const match = text.match(/(-?\d[\d,]*(?:\.\d+)?)\s*(thousand|million|billion|mm|mn|bn|[kmb])?/i);
    if (!match)
        return null;
    const num = Number(match[1].replace(/,/g, ''));
    if (!Number.isFinite(num))
        return null;
    const suffix = (match[2] ?? '').toLowerCase();
    const multiplier = suffix ? (MAGNITUDE[suffix] ?? 1) : 1;
    // Canonical amount is an unformatted numeric string; round to drop float dust.
    const amount = String(Math.round(num * multiplier * 100) / 100);
    return { amount, currency };
}
/**
 * Canonical display string for a currency value: `"<grouped amount> <CODE>"`
 * (e.g. `"5,000,000 USD"`). Returns `''` for empty/garbage so no surface ever
 * renders `"[object Object]"`. Re-normalizes defensively, so it is safe to pass
 * a raw/possibly-corrupt value.
 */
export function formatCurrencyDisplay(value) {
    const normalized = normalizeCurrencyValue(value ?? null);
    if (!normalized)
        return '';
    const num = Number(normalized.amount);
    const amountStr = Number.isFinite(num) ? formatCurrencyAmount(num) : normalized.amount;
    return normalized.currency ? `${amountStr} ${normalized.currency}` : amountStr;
}
/**
 * Formats a numeric dollar amount for display, without a currency symbol.
 *
 * Whole numbers render with no decimal places; fractional amounts render with
 * exactly two decimal places - e.g. `77518 → "77,518"`, `356295.6 → "356,295.60"`,
 * `-39588.4 → "-39,588.40"`. The value is rounded to the cent first so
 * floating-point dust (e.g. `77518.0000001`) does not spuriously gain decimals.
 *
 * Used by both the web UI and the backend export builders so the estimate,
 * resource plan, and exported documents all agree on currency presentation.
 * Callers prepend the `$` (or other currency) symbol themselves.
 */
export function formatCurrencyAmount(value) {
    const rounded = Math.round(value * 100) / 100;
    const hasFraction = !Number.isInteger(rounded);
    return rounded.toLocaleString('en-US', {
        minimumFractionDigits: hasFraction ? 2 : 0,
        maximumFractionDigits: 2,
    });
}
//# sourceMappingURL=currency.js.map